import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import NotificationTable from './components/NotificationTable';
import NotificationModal from './components/NotificationModal';
import { notifications as mockNotifications } from './mockData';
import type { Notification } from './types';

export default function NotificationList() {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>(mockNotifications);
  const [selectedNotification, setSelectedNotification] = useState<Notification | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string>('');

  const handleSearch = (filters: any) => {
    let filtered = [...notifications];

    if (filters.title) {
      filtered = filtered.filter(n => n.title.includes(filters.title));
    }

    if (filters.type) {
      filtered = filtered.filter(n => n.type === filters.type);
    }

    if (filters.target) {
      filtered = filtered.filter(n => n.target === filters.target);
    }

    if (filters.createTimeStart) {
      filtered = filtered.filter(n => n.createTime >= filters.createTimeStart.replace('T', ' ') + ':00');
    }

    if (filters.createTimeEnd) {
      filtered = filtered.filter(n => n.createTime <= filters.createTimeEnd.replace('T', ' ') + ':00');
    }

    if (filters.notificationTimeStart) {
      filtered = filtered.filter(n => n.notificationTime >= filters.notificationTimeStart.replace('T', ' ') + ':00');
    }

    if (filters.notificationTimeEnd) {
      filtered = filtered.filter(n => n.notificationTime <= filters.notificationTimeEnd.replace('T', ' ') + ':00');
    }

    setFilteredNotifications(filtered);
  };

  const handleCreate = () => {
    setSelectedNotification(undefined);
    setShowModal(true);
  };

  const handleEdit = (notification: Notification) => {
    setSelectedNotification(notification);
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    const updated = notifications.filter(n => n.id !== deleteId);
    setNotifications(updated);
    setFilteredNotifications(updated);
    setShowDeleteConfirm(false);
    setDeleteId('');
  };

  const handleSave = (data: Partial<Notification>) => {
    if (data.id) {
      const updated = notifications.map(n =>
        n.id === data.id ? { ...n, ...data } : n
      );
      setNotifications(updated);
      setFilteredNotifications(updated);
    } else {
      const newNotification: Notification = {
        id: String(notifications.length + 1),
        title: data.title!,
        type: data.type!,
        content: data.content!,
        target: data.target!,
        status: 'pending',
        notificationTime: data.notificationTime!,
        createTime: new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        }).replace(/\//g, '-'),
        creator: '当前用户'
      };
      const updated = [newNotification, ...notifications];
      setNotifications(updated);
      setFilteredNotifications(updated);
    }
    setShowModal(false);
  };

  return (
    <MainLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">通知列表</h1>
            <p className="text-sm text-gray-500 mt-1">管理和发送系统通知消息</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-5 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            创建通知
          </button>
        </div>

        <SearchFilters onSearch={handleSearch} />

        <NotificationTable
          notifications={filteredNotifications}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {showModal && (
        <NotificationModal
          notification={selectedNotification}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">确认删除</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">确定要删除这条通知吗？此操作不可恢复。</p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
